// istanbul ignore file
import React, { useEffect } from 'react'
import { CircularProgress, Grid } from '@mui/material'
import Typography from '@mui/material/Typography'
import KpiSummary from './custom-kpi-summary/KpiSummary'
import LanguageStrings from '../../../i18n/locales'
import { fetchAssetDeviceList, fetchKpiDetails, fetchSiteList, fetchKpiConfigs } from '../redux/actionCreators'
import { type RootState } from '../../../store'
import { type ConnectedProps, connect } from 'react-redux'
import { type KpiState } from '../redux/customKpiDetailsSlice'
import AuthError from '../../../components/ErrorComponents/AuthError'
import { APIConfiguration } from '../../../utils/apiConfiguration'
import { GenXDropdown, type Option } from '../../../components/GenXDropdown'
import { type AssetDeviceState } from '../redux/assetListSlice'
import { type SiteListState } from '../redux/siteListSlice'
import { CautionWarning } from './create-custom-kpi/Warnings'
import { type KpiConfigState } from './../redux/deviceKpiParameterSlice'
import './ViewKpiInformation.css'

const kpiDetails = LanguageStrings().ThermalIQStrings.KpiDetailsComponent
const kpiStrings = LanguageStrings().ThermalIQStrings.CreateCustomKpiComponent
const httpStatus = APIConfiguration

interface ViewKpiInformationProps {
  onClose: () => void
  kpiDefinitionId: string
  isAssetLevel: boolean
  assetId: string | null
  assetTypeId: string | null
}

const ViewKpiInformation = (props: Props): JSX.Element => {
  const [site, setSite] = React.useState<Option | null>(null)
  const [asset, setAsset] = React.useState<Option | null>(null)
  const [device, setDevice] = React.useState<Option | null>(null)
  const [assetOptions, setAssetOptions] = React.useState<Option[]>([])
  const [deviceOptions, setDeviceOptions] = React.useState<Option[]>([])
  const [assetIndicators, setAssetIndicators] = React.useState<any>([])

  useEffect(() => {
    props.isAssetLevel
      ? props.fetchKpiDetails(props.kpiDefinitionId, props.assetId ?? undefined)
      : props.fetchSiteList()
    if (props.isAssetLevel && props.assetId !== null && props.assetTypeId !== null) {
      props.fetchKpiConfigs(props.assetId, props.assetTypeId)
    }
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [])

  useEffect(() => {
    if (props.kpiConfigurations.configs !== null && props.kpiConfigurations.configs.length > 0) {
      const selectedAlarm = props.kpiConfigurations.configs.filter((kpi) => kpi.kpiDefinitionId === props.kpiDefinitionId)
      selectedAlarm.length > 0 && setAssetIndicators(selectedAlarm)
    }
  }, [props.kpiConfigurations])

  useEffect(() => {
    if (device?.value != null) {
      props.fetchKpiDetails(props.kpiDefinitionId, device?.value)
      document.body.style.overflow = 'hidden'
      return () => {
        document.body.style.overflow = 'unset'
      }
    }
  }, [device])

  useEffect(() => {
    if (site?.value != null) {
      props.fetchAssetDeviceList(site.value)
    }
  }, [site])

  useEffect(() => {
    if (site?.value != null && asset?.value != null) {
      props.fetchAssetDeviceList(site.value, asset.value)
    }
  }, [asset])

  useEffect(() => {
    if (!props.assetDeviceList.isLoading && props.assetDeviceList !== null && props.assetDeviceList.devices.length === 0) {
      const assetOptions: Option[] = props.assetDeviceList.assets
        .map(asset => ({
          label: asset.name,
          value: asset.id
        }))
      setAssetOptions(assetOptions)
    }
  }, [props.assetDeviceList, site])

  useEffect(() => {
    if (!props.assetDeviceList.isLoading && props.assetDeviceList !== null && props.assetDeviceList.devices.length !== 0) {
      const deviceOptions: Option[] = props.assetDeviceList.devices
        .map(device => ({
          label: device.name,
          value: device.id
        }))
      setDeviceOptions(deviceOptions)
    }
  }, [props.assetDeviceList, asset])

  const siteOptions: Option[] = (props.siteList.sites != null)
    ? props.siteList.sites
      .map(site => ({
        label: site.siteName,
        value: site.id
      }))
    : []

  const handleChooseSite = (value: Option): void => {
    setSite(value)
    setAsset(null)
    setDevice(null)
  }

  const handleChooseAsset = (value: Option): void => {
    setAsset(value)
    setDevice(null)
  }

  const handleChooseDevice = (value: Option): void => {
    setDevice(value)
  }

  const gridSizes = {
    xs: 12,
    sm: 6,
    md: 4,
    lg: 3
  }

  const retry = (): void => {
    props.isAssetLevel
      ? props.fetchKpiDetails(props.kpiDefinitionId, props.assetId ?? undefined)
      : props.fetchKpiDetails(props.kpiDefinitionId, device?.value)
  }

  const handleViewCustomKpiWithFailure = (): JSX.Element => {
    // istanbul ignore else
    if (props.customKpiDetails.httpStatus === httpStatus.SUCCESS_HTTP_STATUS) {
      return (
          <KpiSummary alarmDetails={assetIndicators} customKpiDetails={props.customKpiDetails.kpi} />
      )
    } else if (props.customKpiDetails.isLoading) {
      return (
        <div className='CircularprogressAmi' style={{ flex: 1 }}>
            <CircularProgress />
        </div>
      )
    } else if (props.customKpiDetails.httpStatus === httpStatus.UNAUTHORIZED_HTTP_STATUS) {
      return (
        <div className='authError' style={{ flex: 1 }}>
            <AuthError className={'errorTiqComp'} errorMessage="Unauthorized"/>
        </div>
      )
    } else if (props.customKpiDetails.httpStatus === httpStatus.ACCESS_FORBIDDEN_HTTP_STATUS) {
      return (
        <div className='authError' style={{ flex: 1 }}>
            <AuthError className={'errorTiqComp'} errorMessage="accessForbidden"/>
        </div>
      )
    } else {
      return (
          <div className='authError' style={{ flex: 1 }}>
            <AuthError className={'errorTiqComp'} customError={props.customKpiDetails.error} retry={retry}/>
          </div>
      )
    }
  }

  const handleViewDeviceCustomKpiWithFailure = (): JSX.Element => {
    if (props.customKpiDetails.httpStatus === httpStatus.SUCCESS_HTTP_STATUS) {
      return (
        (site?.value != null && asset?.value != null && device?.value != null)
          ? (
              <KpiSummary customKpiDetails={props.customKpiDetails.kpi} />
            )
          : (
              <div style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <CautionWarning heading={kpiDetails.DropdownSelection} messages={[kpiDetails.DropdownMessage]}/>
              </div>
            )
      )
    } else {
      return (
        handleViewCustomKpiWithFailure()
      )
    }
  }

  return (
    <>
        {!props.isAssetLevel
          ? (
              <>
              <Grid container sx={{
                width: '100%',
                maxHeight: '92%',
                overflow: 'auto',
                marginTop: '24px'
              }} >
                <Grid container sx={{ marginBottom: '30px' }} spacing={2} justifyContent="flex-start">
                  <Grid item xs={gridSizes.xs} sm={gridSizes.sm} md={gridSizes.md} lg={gridSizes.lg}>
                    <Typography sx={{ fontFamily: 'Honeywell Sans Web', fontSize: '16px', letterSpacing: '0.5px' }}>
                      {kpiStrings.Site}
                      <sup style={{ color: '#B0B0B0' }}> *</sup>
                    </Typography>
                    <GenXDropdown
                      placeholder="Select Site"
                      value={site}
                      options={siteOptions}
                      onSelect={handleChooseSite}
                      width={'100%'}
                    />
                  </Grid>
                  <Grid item xs={gridSizes.xs} sm={gridSizes.sm} md={gridSizes.md} lg={gridSizes.lg}>
                    <Typography sx={{ fontFamily: 'Honeywell Sans Web', fontSize: '16px', letterSpacing: '0.5px' }}>
                      {kpiStrings.Asset}
                      <sup style={{ color: '#B0B0B0' }}> *</sup>
                    </Typography>
                    <GenXDropdown
                      placeholder="Select Asset"
                      value={asset}
                      options={assetOptions}
                      onSelect={handleChooseAsset}
                      width={'100%'}
                    />
                  </Grid>
                  <Grid item xs={gridSizes.xs} sm={gridSizes.sm} md={gridSizes.md} lg={gridSizes.lg}>
                    <Typography sx={{ fontFamily: 'Honeywell Sans Web', fontSize: '16px', letterSpacing: '0.5px' }}>
                      {kpiStrings.Device}
                      <sup style={{ color: '#B0B0B0' }}> *</sup>
                    </Typography>
                    <GenXDropdown
                      placeholder="Select Device"
                      value={device}
                      options={deviceOptions}
                      onSelect={handleChooseDevice}
                      width={'100%'}
                    />
                  </Grid>
                </Grid>
                <hr style={{ width: '99%', color: '#808080', backgroundColor: '#808080', height: '1px', border: 'none' }}/>
                {
                  (site?.value != null && asset?.value != null && device?.value != null)
                    ? (
                        handleViewDeviceCustomKpiWithFailure()
                      )
                    : (
                        <div style={{ flexGrow: 1, width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                          <CautionWarning heading={'SELECT SITE, ASSET, DEVICE'} messages={['You have not selected any site, asset and device']}/>
                        </div>
                      )
                }
                </Grid>
              </>
            )
          : (
              <Grid container sx={{
                width: '100%',
                maxHeight: '92%',
                overflow: 'auto',
                marginTop: '24px'
              }} >
                {handleViewCustomKpiWithFailure()}
              </Grid>
            )
        }
    </>
  )
}

interface DispatchToProps {
  fetchKpiDetails: (kpiDefinitionId: string, hierarchyId?: string) => void
  fetchAssetDeviceList: (siteId: string, assetId?: string) => void
  fetchSiteList: () => void
  fetchKpiConfigs: (assetId: string, assetTypeId: string) => void
}

const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  fetchKpiDetails: (kpiDefinitionId: string, hierarchyId?: string) => dispatch(fetchKpiDetails(kpiDefinitionId, hierarchyId)),
  fetchAssetDeviceList: (siteId: string, assetId?: string) => dispatch(fetchAssetDeviceList(siteId, assetId)),
  fetchSiteList: () => dispatch(fetchSiteList()),
  fetchKpiConfigs: (assetId: string, assetTypeId: string) => dispatch(fetchKpiConfigs(assetId, assetTypeId))
})

interface StateToProps {
  customKpiDetails: KpiState
  assetDeviceList: AssetDeviceState
  siteList: SiteListState
  kpiConfigurations: KpiConfigState
}

const mapStateToProps = (state: RootState): StateToProps => ({
  customKpiDetails: state.kpiDetails,
  assetDeviceList: state.tiqAssetList,
  siteList: state.tiqSiteList,
  kpiConfigurations: state.kpiConfigurations
})

const connector = connect(mapStateToProps, mapDispatchToProps)
type ReduxProps = ConnectedProps<typeof connector>
type Props = ViewKpiInformationProps & ReduxProps

export default connector(ViewKpiInformation)
